import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Snackbar, Alert } from '@mui/material';

/**
 * Centraal notificatiesysteem voor transiënte meldingen (toasts).
 *
 * Vervangt de losse, per-pagina `setError`/`setSuccess`-states + inline `<Alert>`'s
 * voor feedback ná een handeling. Paginabrede laadfouten en contextuele banners blijven
 * bewust inline en horen NIET via dit systeem te lopen.
 *
 * Gebruik:
 *   const notify = useNotification();
 *   notify.success('Opgeslagen.');
 *   notify.error('Er ging iets mis.');
 *   notify.warning('Let op...');
 *   notify.info('Ter info...');
 *
 * Meldingen worden één voor één getoond (wachtrij), zijn altijd wegklikbaar (kruisje)
 * en verbergen automatisch: succes/info na 4s, waarschuwing na 5s, fout na 6s.
 */

const NotificationContext = createContext(null);

const AUTO_HIDE_MS = {
    success: 4000,
    info: 4000,
    warning: 5000,
    error: 6000,
};

export function NotificationProvider({ children }) {
    const [queue, setQueue] = useState([]);
    const [current, setCurrent] = useState(null);
    const [open, setOpen] = useState(false);
    const idRef = useRef(0);

    // Zodra er geen melding zichtbaar is en de wachtrij niet leeg is: toon de volgende.
    useEffect(() => {
        if (!current && queue.length > 0) {
            setCurrent(queue[0]);
            setQueue((q) => q.slice(1));
            setOpen(true);
        }
    }, [queue, current]);

    const enqueue = useCallback((message, severity) => {
        if (message === null || message === undefined || message === '') return;
        idRef.current += 1;
        setQueue((q) => [...q, { id: idRef.current, message: String(message), severity }]);
    }, []);

    // Stabiele API-referentie, zodat componenten `notify` veilig in effects/callbacks
    // kunnen gebruiken zonder onnodige re-renders.
    const notifyRef = useRef(null);
    if (notifyRef.current === null) {
        notifyRef.current = {
            success: (message) => enqueue(message, 'success'),
            error: (message) => enqueue(message, 'error'),
            warning: (message) => enqueue(message, 'warning'),
            info: (message) => enqueue(message, 'info'),
            show: (message, severity = 'info') => enqueue(message, severity),
        };
    }

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') return; // niet sluiten bij klik ernaast; alleen timeout of kruisje
        setOpen(false);
    };

    // Na de sluit-animatie: huidige wissen zodat de volgende uit de wachtrij verschijnt.
    const handleExited = () => {
        setCurrent(null);
    };

    return (
        <NotificationContext.Provider value={notifyRef.current}>
            {children}
            {current && (
                <Snackbar
                    key={current.id}
                    open={open}
                    autoHideDuration={AUTO_HIDE_MS[current.severity] ?? 4000}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    TransitionProps={{ onExited: handleExited }}
                >
                    <Alert
                        severity={current.severity}
                        variant="filled"
                        onClose={() => setOpen(false)}
                        sx={{ width: '100%' }}
                    >
                        {current.message}
                    </Alert>
                </Snackbar>
            )}
        </NotificationContext.Provider>
    );
}

export function useNotification() {
    const ctx = useContext(NotificationContext);
    if (!ctx) {
        throw new Error('useNotification moet binnen een NotificationProvider gebruikt worden.');
    }
    return ctx;
}
